import { Box, Container, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import confirmMail from "../../assets/images/icons/verifyMail.svg";
import Button from "../../components/ui/Button";
import Text from "../../components/ui/Text";
import { ROUTES } from "../../constants/routes";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";

const ConfirmEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resendVerificationEmail } = useAuth();
  const { showSuccess, showError } = useToast();

  // Get email from navigation state, default to placeholder
  const email = location.state?.email || "no_email_provided";
  const password = location.state?.password || "";

  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    // If no email in state, redirect to signup
    if (!location.state?.email) {
      navigate(ROUTES.SIGNUP, { replace: true });
    }
  }, [location.state, navigate]);

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResendEmail = async () => {
    if (!email || !password) {
      showError("Unable to resend email. Please sign up again.");
      return;
    }

    setIsResending(true);
    try {
      const result = await resendVerificationEmail(email, password);

      if (result.success) {
        showSuccess("Verification email sent! Check your inbox.");
        setCountdown(60); // Start 60 second countdown
      } else {
        // Email already verified
        showSuccess(result.message);
        navigate(ROUTES.SIGNIN, { replace: true });
      }
    } catch (error) {
      showError(error.friendlyMessage || "Failed to resend verification email");
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToSignin = () => {
    navigate(ROUTES.SIGNIN, { replace: true });
  };

  return (
    <Container>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {/* Title */}
        <Text variant="pageHeader">Confirm your email</Text>
        <Box
          component="img"
          src={confirmMail}
          alt="Confirm your email"
          sx={{
            width: "120px",
            height: "auto",
            mb: 2,
            pt: 2,
          }}
        />
        {/* Description */}
        <Box sx={{ mb: 2, maxWidth: "400px" }}>
          <Text
            variant="paragraph2"
            sx={{
              fontSize: "16px",
              lineHeight: 1.5,
            }}
          >
            We sent an email verification link to{" "}
            <Text
              component="span"
              variant="paragraph2"
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                color: "#000000",
              }}
            >
              {email}
            </Text>
          </Text>
        </Box>

        {/* Instructions */}
        <Box sx={{ mb: 3, maxWidth: "400px" }}>
          <Text
            variant="paragraph3"
            sx={{
              color: "#666666",
              fontSize: "14px",
            }}
          >
            Please check your inbox and click the verification link to activate
            your account. You may need to check your spam folder.
          </Text>
        </Box>

        {/* Action Buttons */}
        <Stack spacing={2} sx={{ width: "100%", maxWidth: "400px", mt: 2 }}>
          <Button
            variant="primary"
            onClick={handleResendEmail}
            disabled={isResending || countdown > 0}
          >
            {isResending
              ? "Sending..."
              : countdown > 0
                ? `Resend in ${countdown}s`
                : "Resend verification email"}
          </Button>

          <Button variant="secondary" onClick={handleBackToSignin}>
            Back to Sign in
          </Button>
        </Stack>

        {/* Additional Help */}
        {/* <Box sx={{ mt: 3 }}>
          <Text
            variant="paragraph3"
            sx={{ color: "#999999", fontSize: "12px" }}
          >
            Having trouble? Contact{" "}
            <a
              href="mailto:support@findaside.com"
              style={{
                color: "#1890FF",
                textDecoration: "none",
              }}
            >
              support@findaside.com
            </a>
          </Text>
        </Box> */}
      </Box>
    </Container>
  );
};

export default ConfirmEmail;
